import { getAllIds, getCountByStatus, useGetAllWritings } from "@/api/writing/writing";
import { readTablePreferences } from "@/hooks/useTablePreferences";
import NewItemDialogTrigger from "@/components/modal/NewItemDialogTrigger";
import StatusTableTab from "@/components/StatusTableTab";
import { AdvancedSearch } from "@/components/table/AdvancedSearch";
import { AdvancedSearchChip } from "@/components/table/AdvancedSearchChip";
import { AdvancedSort } from "@/components/table/AdvancedSort";
import { GlobalSearch } from "@/components/table/GlobalSearch";
import { DashboardListGridLayout } from "@/components/dashboard/DashboardListGridLayout";
import { GridCardWithActions } from "@/components/links/GridCardWithActions";
import { WritingLinkGridCard } from "@/components/links/relatedLinkGridCards";
import { DashboardGridCardActions } from "@/components/table/DashboardGridCardActions";
import { DataTable } from "@/components/ui/datatable";
import NewWritingDialog from "@/components/writing/NewWritingDialog";
import { ColumnFormatted, generateTable } from "@/config/dashboard/general";
import {
  writingsAdvancedSearchConfig,
  writingsColumns,
} from "@/config/dashboard/writings";
import DashboardSelectionBar from "@/components/table/DashboardSelectionBar";
import { useDashboardListScrollContext } from "@/hooks/useDashboardListScrollContext";
import { useDashboardActionsColumn } from "@/hooks/useDashboardActionsColumn";
import { useDashboardParams } from "@/hooks/useDashboardParams";
import { useDashboardRowSelection } from "@/hooks/useDashboardRowSelection";
import { Writing } from "@/lib/types";
import { ColumnDef, VisibilityState } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ACTIONS_COLUMN_ID,
  ensureActionsColumnLast,
} from "@/components/table/table-actions-column";

const STORAGE_KEY = "writing-table-preferences";

const flatVisibleWritingColumns = [
  ...writingsColumns.flatMap((column: ColumnFormatted) =>
    column.visible ? column.key : [],
  ),
  ACTIONS_COLUMN_ID,
];

const WritingDashboard = () => {
  const { t } = useTranslation();

  const {
    fetchParams,
    filterStatus,
    setFilterStatus,
    pagination,
    setPagination,
    countByStatus,
    currentCount,
  } = useDashboardParams<Writing>({
    resourceName: "writing",
    resourceCounter: getCountByStatus,
    statusField: "ecritsPublicationStatus",
  });

  const { data, isFetching } = useGetAllWritings(fetchParams);
  const {
    listKey,
    navigateToFiche,
    entityIds,
    isLoading: listScrollLoading,
    listPagination,
  } = useDashboardListScrollContext(data, pagination, isFetching, STORAGE_KEY);

  const actionsColumn = useDashboardActionsColumn<Writing>({
    documentType: "writing",
    publicationStatusField: "ecritsPublicationStatus",
    onEdit: (row) =>
      navigateToFiche(`/edit/writing/${row.id}`, row.id),
  });

  const columns: ColumnDef<Writing, any>[] = useMemo(() => {
    return [...generateTable(writingsColumns, "writing"), actionsColumn];
  }, [actionsColumn]);


  const [columnsOrder, setColumnsOrder] = useState<string[]>(() => {
    const saved = readTablePreferences(STORAGE_KEY);
    if (saved) {
      const { order } = saved;
      return ensureActionsColumnLast(order || flatVisibleWritingColumns);
    }
    return flatVisibleWritingColumns;
  });

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    () => {
      const saved = readTablePreferences(STORAGE_KEY);
      if (saved) {
        const { visibility } = saved;
        return (
          visibility || {
            ...writingsColumns.reduce(
              (acc, column) => ({
                ...acc,
                [column.key]: column.visible || false,
              }),
              {},
            ),
          }
        );
      }
      return {
        ...writingsColumns.reduce(
          (acc, column) => ({
            ...acc,
            [column.key]: column.visible || false,
          }),
          {},
        ),
      };
    },
  );

  const {
    selectionState,
    setSelectionState,
    dataTableSelection,
    isSelected,
    setRowSelected,
  } = useDashboardRowSelection<Writing>({
      getAllRowIds: () => getAllIds(fetchParams.where),
    });

  return (
    <div className="flex flex-col items-start gap-3">
      <div className="flex justify-between  items-center w-full">
        <p className="text-[32px] font-[600]">{t("dashboardTitleWrittings")}</p>
        <GlobalSearch resource="writing" config={writingsAdvancedSearchConfig} data={data ?? []} />
      </div>
      <div className="flex flex-row justify-between w-full">
        <StatusTableTab
          filterStatus={filterStatus}
          countByStatus={countByStatus}
          onValueChange={setFilterStatus}
        />
        <div className="flex flex-row gap-2">
          <AdvancedSearch
            config={writingsAdvancedSearchConfig}
            resource="writing"
          />
          <AdvancedSort config={writingsAdvancedSearchConfig} />
          <NewItemDialogTrigger
            title={t("createWwriting")}
            Dialog={NewWritingDialog}
            batchImportType="writing"
          />
        </div>
      </div>
      <div className="w-full flex flex-row">
        <AdvancedSearchChip />
      </div>
      <div className="w-full">
        <DashboardListGridLayout
          storageKey={STORAGE_KEY}
          listKey={listKey}
          entityIds={entityIds}
          isLoading={listScrollLoading}
          listPagination={listPagination}
          setListPagination={setPagination}
          listTable={
            <DataTable
              onRowClick={(row) =>
                navigateToFiche(`/edit/writing/${row.original.id}`, row.original.id)
              }
              getListItemId={(row) => row.original.id}
              pagination={{
                pagination: pagination,
                setPage: setPagination,
                totalSize: currentCount,
              }}
              columns={columns}
              data={data ?? []}
              storageKey={STORAGE_KEY}
              options={{
                enableColumnResizing: true,
                state: {
                  columnVisibility: columnVisibility,
                  columnOrder: columnsOrder,
                },
                onColumnVisibilityChange: setColumnVisibility,
                onColumnOrderChange: setColumnsOrder,
              }}
              selection={dataTableSelection}
            />
          }
          gridContent={
            <>
              {(data ?? []).map((item) => (
                <GridCardWithActions
                  key={item.id}
                  selected={isSelected(item.id)}
                  onSelectedChange={(checked) =>
                    setRowSelected(item.id, checked)
                  }
                  actions={
                    <DashboardGridCardActions
                      documentType="writing"
                      item={item}
                      publicationStatusField="ecritsPublicationStatus"
                      onEdit={(row) =>
                        navigateToFiche(`/edit/writing/${row.id}`, row.id)
                      }
                    />
                  }
                >
                  <WritingLinkGridCard
                    item={item}
                    t={t}
                    onTextClick={() =>
                      navigateToFiche(`/edit/writing/${item.id}`, item.id)
                    }
                  />
                </GridCardWithActions>
              ))}
            </>
          }
          pagination={{
            pagination: pagination,
            setPage: setPagination,
            totalSize: currentCount,
          }}
        />
        <DashboardSelectionBar
          documentType="writing"
          selectionState={selectionState}
          setSelectionState={setSelectionState}
        />
      </div>
    </div>
  );
};

export default WritingDashboard;
